import {assert} from 'Asserts';

import {getFieldInfoFromDataFlowEdgeHandle} from '../../flowchartIdentifierUtils';
import {
  CustomMappingData,
  FlowchartStaticEdgeData,
  ProcessFlowchartEdgeType,
  TransitionSource,
} from '../../processFlowchartConfigEditorConstants';
import {
  StaticFlowchartNodeData,
  TaskSchemaNodeSectionData,
  TaskSchemaStaticNodeData,
  isStaticTaskSchemaNodeData,
} from '../../processFlowchartConfigEditorNodeTypes';
import {getDefaultTransitionSource} from '../../TransitionSourceUtils';
import {getFieldPairKey, getPTDVPairKey} from './transitionKeyUtils';
import {Transition} from './TransitionTypes';

/**
 * Common interface for nodes that have PTDV information.
 * Works with both static nodes and interactive node data.
 */
export interface NodeWithPTDV {
  id: string;
  taskDefinitionVersionSpec: {clientId: string};
}

/**
 * Type guard to check if a node has PTDV information.
 */
export function isNodeWithPTDV(node: {
  id: string;
  taskDefinitionVersionSpec?: {clientId: string};
}): node is NodeWithPTDV {
  return node.taskDefinitionVersionSpec?.clientId != null;
}

/**
 * Extract PTDV client ID from a node.
 * Works with TaskSchemaDomainData, TaskSchemaStaticNodeData, or any node with taskDefinitionVersionSpec.
 */
export function getPTDVIdFromNode(node: NodeWithPTDV): string {
  return node.taskDefinitionVersionSpec.clientId;
}

export interface NodePair<T> {
  sourceNode: T;
  targetNode: T;
}

export type TaskSchemaNodePair = NodePair<TaskSchemaStaticNodeData>;

export function findNodePairByIds<T extends {id: string}>(
  sourceNodeId: string,
  targetNodeId: string,
  nodes: Array<T>
): NodePair<T> | null {
  const sourceNode = nodes.find((n) => n.id === sourceNodeId);
  const targetNode = nodes.find((n) => n.id === targetNodeId);

  if (!sourceNode || !targetNode) {
    return null;
  }

  return {sourceNode, targetNode};
}

export function findTaskSchemaNodePair(
  edge: FlowchartStaticEdgeData,
  nodes: Array<StaticFlowchartNodeData>
): TaskSchemaNodePair | null {
  const nodePair = findNodePairByIds(edge.source, edge.target, nodes);
  if (!nodePair) {
    return null;
  }

  if (!isStaticTaskSchemaNodeData(nodePair.sourceNode) || !isStaticTaskSchemaNodeData(nodePair.targetNode)) {
    return null;
  }

  return {sourceNode: nodePair.sourceNode, targetNode: nodePair.targetNode};
}

export interface HasTransitionForEdgeParams {
  edge: FlowchartStaticEdgeData;
  nodes: Array<StaticFlowchartNodeData>;
  ptdvPairToTransitionIdIndex: Record<string, Array<string>>;
}

export function hasTransitionForEdge({
  edge,
  nodes,
  ptdvPairToTransitionIdIndex,
}: HasTransitionForEdgeParams): boolean {
  const nodePair = findTaskSchemaNodePair(edge, nodes);
  if (!nodePair) {
    return false;
  }
  const sourcePTDVId = nodePair.sourceNode.taskDefinitionVersionSpec.clientId;
  const targetPTDVId = nodePair.targetNode.taskDefinitionVersionSpec.clientId;
  const pairKey = getPTDVPairKey(sourcePTDVId, targetPTDVId);
  return (ptdvPairToTransitionIdIndex[pairKey]?.length ?? 0) > 0;
}

export interface GetTransitionSourceForEdgeParams {
  edge: FlowchartStaticEdgeData;
  nodes: Array<StaticFlowchartNodeData>;
  transitionsById: Record<string, Transition>;
  /** Index of transition IDs by source + target PTDV pair (each pair maps to an array of IDs) */
  ptdvPairToTransitionIdIndex: Record<string, Array<string>>;
}

export function getTransitionSourceForEdge({
  edge,
  nodes,
  transitionsById,
  ptdvPairToTransitionIdIndex,
}: GetTransitionSourceForEdgeParams): TransitionSource | null {
  const nodePair = findTaskSchemaNodePair(edge, nodes);
  if (!nodePair) {
    return null;
  }

  return getTransitionSourceForNodePair({
    sourceNodeId: edge.source,
    targetNodeId: edge.target,
    nodes: [nodePair.sourceNode, nodePair.targetNode],
    transitionsById,
    ptdvPairToTransitionIdIndex,
  });
}

export type GetCustomMappingForEdgeParams = GetTransitionSourceForEdgeParams;

export function getCustomMappingForEdge({
  edge,
  nodes,
  transitionsById,
  ptdvPairToTransitionIdIndex,
}: GetCustomMappingForEdgeParams): CustomMappingData | null {
  assert(edge.type === ProcessFlowchartEdgeType.DATA_FLOW, 'Edge must be a data flow edge');

  const nodePair = findTaskSchemaNodePair(edge, nodes);
  if (!nodePair) {
    return null;
  }

  const sourceHandle = getFieldInfoFromDataFlowEdgeHandle(edge.sourceHandle);
  const targetHandle = getFieldInfoFromDataFlowEdgeHandle(edge.targetHandle);

  const sourcePTDVId = nodePair.sourceNode.taskDefinitionVersionSpec.clientId;
  const targetPTDVId = nodePair.targetNode.taskDefinitionVersionSpec.clientId;

  const pairKey = getPTDVPairKey(sourcePTDVId, targetPTDVId);
  const transitionIds = ptdvPairToTransitionIdIndex[pairKey];
  const transitionId = transitionIds?.[0];
  const transition = transitionId ? transitionsById[transitionId] : null;

  if (!transition) {
    return null;
  }

  const fieldPairKey = getFieldPairKey({
    sourcePTDVId,
    sourceSectionType: sourceHandle.sectionType,
    sourceFieldClientId: sourceHandle.fieldClientId,
    targetPTDVId,
    targetSectionType: targetHandle.sectionType,
    targetFieldClientId: targetHandle.fieldClientId,
  });

  return transition.customMappingDataByFieldPair[fieldPairKey] ?? null;
}

export interface GetTransitionSourceForNodePairParams {
  sourceNodeId: string;
  targetNodeId: string;
  nodes: Array<NodeWithPTDV>;
  transitionsById: Record<string, Transition>;
  ptdvPairToTransitionIdIndex: Record<string, Array<string>>;
}

/**
 * Get transition source by source and target node IDs.
 * Returns null if nodes are not found or no transition exists.
 */
export function getTransitionSourceForNodePair({
  sourceNodeId,
  targetNodeId,
  nodes,
  transitionsById,
  ptdvPairToTransitionIdIndex,
}: GetTransitionSourceForNodePairParams): TransitionSource | null {
  const nodePair = findNodePairByIds(sourceNodeId, targetNodeId, nodes);
  if (!nodePair) {
    return null;
  }

  const sourcePTDVId = getPTDVIdFromNode(nodePair.sourceNode);
  const targetPTDVId = getPTDVIdFromNode(nodePair.targetNode);

  const pairKey = getPTDVPairKey(sourcePTDVId, targetPTDVId);
  const transitionIds = ptdvPairToTransitionIdIndex[pairKey];
  const transitionId = transitionIds?.[0];
  const transition = transitionId ? transitionsById[transitionId] : null;

  if (!transition) {
    return null;
  }

  return transition.transitionSourcesBySourcePTDVId[sourcePTDVId] ?? null;
}

export interface NodeWithSections {
  id: string;
  sections?: Array<TaskSchemaNodeSectionData>;
}

export interface WithDefaultParams {
  nodesForDefault: Array<NodeWithSections>;
}

export type GetTransitionSourceForNodePairWithDefaultParams = GetTransitionSourceForNodePairParams &
  WithDefaultParams;

/**
 * Get transition source by source and target node IDs, with default fallback.
 * Returns the default transition source based on whether the source node has outputs.
 */
export function getTransitionSourceForNodePairWithDefault({
  sourceNodeId,
  targetNodeId,
  nodes,
  transitionsById,
  ptdvPairToTransitionIdIndex,
  nodesForDefault,
}: GetTransitionSourceForNodePairWithDefaultParams): TransitionSource {
  const transitionSource = getTransitionSourceForNodePair({
    sourceNodeId,
    targetNodeId,
    nodes,
    transitionsById,
    ptdvPairToTransitionIdIndex,
  });

  if (transitionSource != null) {
    return transitionSource;
  }

  return getDefaultTransitionSource(sourceNodeId, nodesForDefault);
}

export type GetTransitionSourceForEdgeWithDefaultParams = GetTransitionSourceForEdgeParams &
  WithDefaultParams;

/**
 * Get transition source for an edge, with default fallback.
 * Returns the default transition source based on whether the source node has outputs.
 */
export function getTransitionSourceForEdgeWithDefault({
  edge,
  nodes,
  transitionsById,
  ptdvPairToTransitionIdIndex,
  nodesForDefault,
}: GetTransitionSourceForEdgeWithDefaultParams): TransitionSource {
  const transitionSource = getTransitionSourceForEdge({
    edge,
    nodes,
    transitionsById,
    ptdvPairToTransitionIdIndex,
  });

  if (transitionSource != null) {
    return transitionSource;
  }

  return getDefaultTransitionSource(edge.source, nodesForDefault);
}
